import { completeStage, selectStage } from '../../redux/slices/stageSlice';
import {
	display,
	fetchReservationProfiles,
	selectSession,
	setGuestVerificationLazyLoad,
} from '../../redux/slices/sessionSlice';
import {
	downloadRegistrationCard,
	selectProfile,
	sendRegistrationCard,
} from '../../redux/slices/profileSlice';
import {
	faCheckCircle,
	faDownload,
	faEnvelope,
	faHome,
} from '@fortawesome/free-solid-svg-icons';
import { getRegCardStatus, retryRegCardQueue } from '../../redux/api';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hook';

import { bypassConfirmLeaveOnce } from '../../hooks/confirmLeavePageBypass';
import { DarkButton } from '../../components/dark-button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

type ReservationProfile = {
	profileID?: number;
	firstName?: string | null;
	lastName?: string | null;
	placeholderName?: string;
	isPrimary?: boolean;
	isChild?: boolean;
	isGuestForPrecheckin?: boolean;
	verificationStatus?: string | null;
};

const toTitleCase = (text?: string | null) => {
	if (!text) return '';

	const words = text.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '';

	return words
		.map((word) => {
			const lower = word.toLowerCase();
			return lower[0].toUpperCase() + lower.slice(1);
		})
		.join(' ');
};

export default function SendRegistrationCardHome() {
	const dispatch = useAppDispatch();
	const { token, emailPrimary, reservation, propertyInfo, profiles } =
		useAppSelector(selectSession);
	const { currentProfile } = useAppSelector(selectProfile);
	const { stages, stage } = useAppSelector(selectStage);
	const { t } = useTranslation();
	const targetEmail =
		currentProfile?.email || currentProfile?.emailAddress || emailPrimary;

	const [isDownloading, setIsDownloading] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [isCompleting, setIsCompleting] = useState(false);
	const [emailInput, setEmailInput] = useState<string>(targetEmail || '');
	const [emailError, setEmailError] = useState<string>('');
	const [complete, setComplete] = useState(false);

	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const profileList: ReservationProfile[] = Array.isArray(profiles)
		? profiles
		: [];
	const hasGuestForPrecheckin = profileList.some(
		(profile) => profile?.isGuestForPrecheckin === true
	);
	// Mirrors the eligibility rules of the guest list: when a profile is flagged
	// for pre-check-in only those guests are expected to complete it.
	const requiredProfiles = hasGuestForPrecheckin
		? profileList.filter((profile) => profile?.isGuestForPrecheckin === true)
		: profileList;
	const isProfileComplete = (profile: ReservationProfile) => {
		const status = (profile?.verificationStatus ?? '').toLowerCase();
		if (status === 'completed' || status === 'verified') return true;
		// The guest on this screen has just finished their own check-in, so the
		// locally held profile may still carry a stale "Pending" status.
		return (
			!!currentProfile?.profileID &&
			profile?.profileID === currentProfile?.profileID
		);
	};
	const pendingProfiles = requiredProfiles.filter(
		(profile) => !isProfileComplete(profile)
	);
	const profilesReady = profileList.length > 0;
	const allGuestsComplete = profilesReady && pendingProfiles.length === 0;
	const hasPendingGuests = pendingProfiles.length > 0;

	useEffect(() => {
		return () => {
			if (pollRef.current) {
				clearInterval(pollRef.current as any);
				pollRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (
			!token ||
			!reservation?.PmsConfirmationNo ||
			currentProfile?.isPrimary === false
		)
			return;

		dispatch(
			fetchReservationProfiles({
				token,
				confirmationNo: reservation.PmsConfirmationNo,
			})
		);
	}, [
		dispatch,
		token,
		reservation?.PmsConfirmationNo,
		currentProfile?.isPrimary,
	]);

	useEffect(() => {
		// Do not walk away while other guests still have to check in.
		if (hasPendingGuests) return;

		let timer: ReturnType<typeof setTimeout>;

		const resetTimer = () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				bypassConfirmLeaveOnce();
				window.location.href = '/';
			}, 30000);
		};

		resetTimer();

		const events = ['click', 'touchstart', 'keydown', 'pointerdown'];
		events.forEach((event) => window.addEventListener(event, resetTimer));

		return () => {
			if (timer) clearTimeout(timer);
			events.forEach((event) => window.removeEventListener(event, resetTimer));
		};
	}, [hasPendingGuests]);

	const validateEmail = (email: string) => {
		if (!email) {
			return 'Email is required';
		}
		const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
		if (!emailRegex.test(email)) {
			return 'Invalid email format';
		}
		return '';
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setEmailInput(val);
		setEmailError(validateEmail(val));
	};

	useEffect(() => {
		if (targetEmail && !emailInput) {
			setEmailInput(targetEmail);
			setEmailError(validateEmail(targetEmail));
		}
	}, [targetEmail]);

	useEffect(() => {
		if (currentProfile && currentProfile.isPrimary === false) {
			dispatch(display(true));
			setComplete(true);
		}
	}, [currentProfile?.isPrimary, dispatch]);

	useEffect(() => {
		console.log(currentProfile, '#currentProfile#');
	}, []);

	useEffect(() => {
		if (complete) {
			dispatch(
				completeStage({
					token,
					stageID: stage?.stepID,
					ReservationID: reservation?.ReservationID,
					type: 'checkin',
					confirmationNo: reservation?.PmsConfirmationNo,
					stages,
					propertyCode: propertyInfo?.propertyCode ?? '',
					profileID: currentProfile?.profileID ?? null,
					refreshProfiles: true,
				})
			).finally(() => {
				dispatch(display(false));
			});
		}
	}, [
		complete,
		currentProfile?.profileID,
		dispatch,
		propertyInfo?.propertyCode,
		reservation?.PmsConfirmationNo,
		reservation?.ReservationID,
		stage?.stepID,
		stages,
		token,
	]);

	/**
	 * Handle download registration card as PDF
	 */
	const handleDownloadCard = async () => {
		try {
			setIsDownloading(true);

			await dispatch(downloadRegistrationCard({ token })).unwrap();

			// ✅ Only runs AFTER download completes
			Swal.fire({
				icon: 'success',
				title: 'Downloaded Successfully',
				text: 'Your registration card has been downloaded.',
				confirmButtonColor: 'var(--primary-color)',
			});
		} catch (error) {
			console.error('Error downloading registration card:', error);

			Swal.fire({
				icon: 'error',
				title: 'Download Failed',
				text: 'Failed to download registration card. Please try again.',
				confirmButtonColor: 'var(--primary-color)',
			});
		} finally {
			setIsDownloading(false);
		}
	};
	/**
	 * Handle email registration card
	 */
	const handleEmailCard = async () => {
		if (!token || !emailInput) {
			Swal.fire({
				icon: 'error',
				title: 'Unable to Send',
				text: 'Missing email address',
				confirmButtonColor: 'var(--primary-color)',
			});
			return;
		}

		const errorMsg = validateEmail(emailInput);
		if (errorMsg) {
			setEmailError(errorMsg);
			Swal.fire({
				icon: 'error',
				title: 'Invalid Email',
				text: errorMsg,
				confirmButtonColor: 'var(--primary-color)',
			});
			return;
		}

		try {
			setIsSendingEmail(true);
			setEmailError(''); // clear old error

			// Inform user we're preparing the registration card
			Swal.fire({
				html: 'Please wait while we prepare your registration card...',
				allowOutsideClick: false,
				didOpen: () => {
					Swal.showLoading();
				},
				confirmButtonColor: 'var(--primary-color)',
			});

			// Poll every second for reg card ready status
			let tries = 0;
			const maxTries = 60; // 60 seconds timeout
			let retryCalled = false; // ensure we only call requeue once to avoid memory issues

			pollRef.current = setInterval(async () => {
				tries += 1;
				try {
					const resp = await getRegCardStatus({
						token,
						confirmationNo: reservation?.PmsConfirmationNo,
					});

					const data = resp?.data;
					const ready =
						data === true || data?.result === true || data?.Result === true;

					if (ready) {
						if (pollRef.current) {
							clearInterval(pollRef.current as any);
							pollRef.current = null;
						}
						Swal.close();

						// Now send the email
						const response = await dispatch(
							sendRegistrationCard({ token, email: emailInput })
						).unwrap();

						if (response?.result === true) {
							await Swal.fire({
								icon: 'success',
								title: 'Email Sent',
								text: `Registration card will be sent to ${emailInput}`,
								timer: 1500,
								showConfirmButton: false,
								confirmButtonColor: 'var(--primary-color)',
							});
							setComplete(true);
						} else {
							Swal.fire({
								icon: 'error',
								title: 'Email Failed',
								text: `Failed to process email for ${emailInput}. Please try again.`,
								confirmButtonColor: 'var(--primary-color)',
							});
						}
						setIsSendingEmail(false);
						return;
					}

					// If not ready, attempt to requeue generation once after 10 tries
					if (!ready) {
						if (!retryCalled && tries >= 10) {
							try {
								await retryRegCardQueue({
									token,
									confirmationNo: reservation?.PmsConfirmationNo,
									profileID: currentProfile?.profileID ?? 0,
								});
								retryCalled = true;
								console.log('RetryRegCardQueue called once after 10 tries');
							} catch (e) {
								console.error('RetryRegCardQueue failed', e);
							}
						}
					}

					if (tries >= maxTries) {
						if (pollRef.current) {
							clearInterval(pollRef.current as any);
							pollRef.current = null;
						}
						Swal.close();
						setIsSendingEmail(false);
						Swal.fire({
							icon: 'error',
							title: 'Timeout',
							text: 'Preparing registration card is taking longer than expected. Please try again later.',
							confirmButtonColor: 'var(--primary-color)',
						});
					}
				} catch (err) {
					console.error('Error checking reg card status:', err);
					if (pollRef.current) {
						clearInterval(pollRef.current as any);
						pollRef.current = null;
					}
					Swal.close();
					setIsSendingEmail(false);
					Swal.fire({
						icon: 'error',
						title: 'Error',
						text: 'Failed to check registration card status. Please try again.',
						confirmButtonColor: 'var(--primary-color)',
					});
				}
			}, 1000);
		} catch (err: any) {
			console.error('Error initiating send registration card flow:', err);
			setIsSendingEmail(false);
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Failed to start registration card preparation. Please try again.',
				confirmButtonColor: 'var(--primary-color)',
			});
		}
	};
	/**
	 * Handle Home click - complete stage and reload
	 */
	const handleHomeClick = async () => {
		try {
			dispatch(setGuestVerificationLazyLoad(true));
			setIsCompleting(true);
			await dispatch(
				completeStage({
					token,
					stageID: stage?.stepID,
					ReservationID: reservation?.ReservationID,
					type: 'checkin',
					confirmationNo: reservation?.PmsConfirmationNo,
					stages,
					propertyCode: propertyInfo?.propertyCode ?? '',
					profileID: currentProfile?.profileID ?? null,
					refreshProfiles: true,
				})
			).unwrap();

			bypassConfirmLeaveOnce();
			window.location.reload();
		} catch (error) {
			console.error('Error completing stage:', error);
		} finally {
			setIsCompleting(false);
			// dispatch(setGuestVerificationLazyLoad(false));
		}
	};

	if (currentProfile && currentProfile.isPrimary === false) {
		return null;
	}

	return (
		<div className="flex flex-col items-center w-full px-4 pt-6 pb-28">
			{/* Success Card */}
			<div className="w-full max-w-md bg-[#E9F5F6] border border-[#E9F5F6] rounded-2xl p-6 flex flex-col items-center text-center mb-6">
				<div className="w-14 h-14 flex items-center justify-center text-[#00909A] mb-4">
					{/* <FontAwesomeIcon icon={faCheckCircle} /> */}
					<img
						className="align-middle"
						src={process.env.PUBLIC_URL + '/assets/icons/verify.png'}
						alt="Back"
					/>
				</div>

				<p className="text-sm text-gray-600 mb-1">
					{t('ThankyouPageMessage1')}
				</p>

				<h2 className="text-lg font-semibold text-[#00909A]">
					{allGuestsComplete
						? t('PreCheckinCompleted')
						: t('LastPageYourCheckinComplete')}
				</h2>

				{profilesReady && (
					<p className="text-sm text-gray-600 mt-1">
						{allGuestsComplete
							? t('LastPageAllGuestsVerified')
							: t('LastPageGuestsRemaining', {
									remaining: pendingProfiles.length,
									total: requiredProfiles.length,
							  })}
					</p>
				)}
			</div>

			{/* Guests still waiting to check in */}
			{hasPendingGuests && (
				<div className="w-full max-w-md bg-white border border-gray-300 rounded-xl p-4 flex flex-col gap-3 mb-6">
					<div className="flex items-center justify-between">
						<h3 className="text-base font-semibold text-gray-800">
							{t('LastPagePendingGuestsTitle')}
						</h3>
						<span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#E9F5F6] text-[#00909A]">
							{pendingProfiles.length}
						</span>
					</div>

					{pendingProfiles.map((profile, index) => {
						const fullName = `${profile.firstName ?? ''} ${
							profile.lastName ?? ''
						}`.trim();
						const displayName =
							fullName ||
							profile.placeholderName ||
							(profile.isChild ? 'Child' : 'Guest');
						const status = (profile.verificationStatus ?? '').toLowerCase();

						return (
							<div
								key={
									profile.profileID && profile.profileID > 0
										? profile.profileID
										: `pending-guest-${index}`
								}
								className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2"
								style={{ backgroundColor: 'var(--opaque-color)' }}>
								<div className="flex items-center gap-2 min-w-0">
									<img
										src={process.env.PUBLIC_URL + '/guest_icon.png'}
										alt=""
										style={{
											width: 20,
											height: 20,
											objectFit: 'contain',
											flexShrink: 0,
										}}
									/>
									<span className="text-sm text-gray-700 truncate">
										{toTitleCase(displayName)}
									</span>
								</div>
								<span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 flex-shrink-0">
									{status === 'shared'
										? t('LastPageStatusInviteSent')
										: status === 'pending'
										? t('LastPageStatusInProgress')
										: t('LastPageStatusNotStarted')}
								</span>
							</div>
						);
					})}
				</div>
			)}

			{/* Registration Card */}
			<div className="w-full max-w-md bg-white border border-gray-300 rounded-xl p-4 flex flex-col gap-3">
				<h3 className="text-lg font-semibold text-gray-800">
					Registration Card
				</h3>

				<p className="text-sm text-gray-500">
					{t(
						'Your registration card is ready. How would you like to receive it?'
					)}
				</p>

				{/* Email Input */}
				<div className="flex flex-col gap-1">
					<label className="text-sm text-gray-600">Email Address</label>

					<input
						type="email"
						value={emailInput}
						onChange={handleEmailChange}
						disabled={true}
						placeholder="Enter email address"
						className={`w-full px-3 py-2 form-control ${
							emailError
								? 'border-red-400 focus:ring-red-300'
								: 'border-gray-300 focus:ring-[#00909A]'
						}`}
					/>

					{emailError && (
						<span className="text-xs text-red-500">{emailError}</span>
					)}
				</div>

				{/* Buttons */}
				<div className="flex flex-col gap-3 mt-2">
					<button
						onClick={handleEmailCard}
						disabled={isSendingEmail || !!emailError}
						className="w-full flex items-center justify-center gap-2 bg-[#00909A] text-white py-2 rounded-md text-sm disabled:opacity-50">
						<FontAwesomeIcon icon={faEnvelope} />
						{isSendingEmail ? 'Sending...' : 'Email To Me'}
					</button>

					{/* <button
						onClick={handleDownloadCard}
						disabled={isDownloading}
						className="w-full flex items-center justify-center gap-2 border border-[#00909A] text-[#00909A] py-2 rounded-md text-sm disabled:opacity-50">
						<FontAwesomeIcon icon={faDownload} />
						isDownloading ? 'Downloading...' : 'Download'}
					</button> */}
				</div>
			</div>

			{/* Bottom Fixed Button */}
			<div className="fixed-bottom w-100 px-3 px-md-5 py-3 border-top theme-footer d-flex justify-content-center">
				<DarkButton
					onClick={handleHomeClick}
					disabled={isCompleting}
					text={
						hasPendingGuests ? t('LastPageCompleteRemaining') : t('Go to Home')
					}
					filled
					hideArrow
				/>
			</div>
		</div>
	);
}
